import { DestroyRef, WritableSignal } from '@angular/core';
import { NEVER, Observable, Subject, concat, firstValueFrom, takeUntil } from 'rxjs';

import { ActionState } from '@shared/state/async-action';
import { OrgError } from '../domain/org.errors';
import { OrgErrorMapper } from './org-error.mapper';

/**
 * The two helpers every B2B facade runs its calls through, so the loading,
 * cancellation and error-capture rules are written once rather than per facade.
 *
 * {@link ActionState} (shared, used by seven domains already) still owns the
 * `idle → loading → success → error` lifecycle and the duplicate-execution
 * guard. What it cannot do is hand the caught error back — `run()` swallows it
 * and returns null — so {@link runOrgAction} re-maps the failure into an
 * {@link OrgError} sink on the way past, and lets the original error continue
 * into `ActionState`'s own catch. One await, two pieces of state, no duplication.
 */

/**
 * Run `op` under `state`, capturing the mapped B2B failure in `sink`.
 *
 * `sink` is cleared on entry so a retry never renders the previous attempt's
 * message underneath the new spinner — which is what keeps §5's "every failure
 * leaves the UI recoverable" true: loading clears, the error is replaced rather
 * than accumulated, and the action can simply be called again.
 */
export async function runOrgAction<T>(
  state: ActionState,
  sink: WritableSignal<OrgError | null>,
  mapper: OrgErrorMapper,
  op: () => Promise<T>,
): Promise<T | null> {
  return state.run(async () => {
    sink.set(null);
    try {
      return await op();
    } catch (err: unknown) {
      sink.set(mapper.map(err));
      throw err; // ActionState records `failed` + its own message from the same throw
    }
  });
}

/**
 * `firstValueFrom`, but the subscription dies with the injection context.
 *
 * Facades are route-scoped, so a member who leaves the surface mid-request has
 * no interest in the answer — and without this the pending HTTP call would run
 * to completion and write into a store nobody is reading. `takeUntil` on a
 * `DestroyRef`-fed subject unsubscribes, which aborts the underlying request.
 *
 * The promise then never settles, deliberately — `concat(…, NEVER)` turns the
 * completion `takeUntil` emits into "no answer ever". That is the same shape SSR
 * already relies on (`AppHttp.process()` returns `NEVER` on the server), and it
 * is the only ending that is not a lie: resolving would write `undefined` into a
 * store nobody reads, and rejecting (which a bare `firstValueFrom` does on an
 * empty completion, with `EmptyError`) would flash a spurious error state onto a
 * screen that is already gone.
 */
export function firstValueFromScoped<T>(source: Observable<T>, destroyed: Subject<void>): Promise<T> {
  return firstValueFrom(concat(source.pipe(takeUntil(destroyed)), NEVER));
}

/**
 * The teardown subject a facade feeds {@link firstValueFromScoped}. Call once in
 * a facade's field initialiser, inside an injection context.
 */
export function destroyNotifier(destroyRef: DestroyRef): Subject<void> {
  const destroyed = new Subject<void>();
  destroyRef.onDestroy(() => {
    destroyed.next();
    destroyed.complete();
  });
  return destroyed;
}
